import { Link } from "react-router-dom";
import {
  ArrowRight, FileSearch, Scale, ShieldCheck, RefreshCw, Banknote, XCircle,
  ClipboardList, AlertTriangle, BadgeCheck, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/lib/seo";
import {
  Reveal, SectionHeading, CTASection, ComplianceNote, CheckItem, FAQSection, ProcessTimeline, stagger, fadeUp,
} from "@/components/shared/Shared";
import { motion } from "framer-motion";

const IMG_HERO = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1600&auto=format&fit=crop";

export default function Maklerservice() {
  const ablauf = [
    { title: "Drei Angaben genügen", text: "Tarif bzw. Versicherer, aktueller Rückkaufswert und monatliche Sparrate – mehr brauchen wir für den Einstieg nicht." },
    { title: "Analyse in 48 Stunden", text: "Wir prüfen Kostenstruktur und Vertragsstand – und erstellen ein individuelles Konzept, in der Regel innerhalb von 48 Stunden." },
    { title: "Optionen besprechen", text: "Tarifumstellung, Verwertung des Altvertrags oder Beibehalt: Sie sehen alle Wege mit Vor- und Nachteilen – dokumentiert und verständlich." },
    { title: "Digitale Umsetzung", text: "Wenn Sie sich entscheiden, läuft die Umsetzung digital – unter anderem über die EdoBox. Ohne Papierstapel, ohne Vorabkosten." },
  ];

  const wege = [
    {
      icon: RefreshCw,
      title: "Tarifumstellung",
      text: "Wechsel in einen kostengünstigeren Tarif – häufig beim selben Versicherer. Bestehende Garantien und Ihr ursprüngliches Eintrittsalter bleiben dabei erhalten.",
      punkte: ["Garantien bleiben bestehen", "Eintrittsalter bleibt erhalten", "Digitale Abwicklung"],
    },
    {
      icon: Banknote,
      title: "Verwertung des Altvertrags",
      text: "Statt zu kündigen, kann ein Altvertrag über spezialisierte Partner verwertet werden: durch Aufkauf (Pro Life) – die Auszahlung kann über dem Rückkaufswert liegen – oder über die Prüfung einer Rückabwicklung (Ascon Consulting), etwa bei fehlerhafter Widerrufsbelehrung.",
      punkte: ["Aufkauf statt Kündigung möglich", "Abwicklung i. d. R. 14–21 Werktage", "Rückabwicklungsprüfung häufig auf Erfolgsbasis"],
    },
    {
      icon: ShieldCheck,
      title: "Beibehalt",
      text: "Nicht jeder Vertrag ist schlecht. Wenn Ihr bestehender Vertrag gut ist, sagen wir Ihnen das genauso klar – und Sie behalten ihn.",
      punkte: ["Ehrliches Ergebnis, dokumentiert", "Keine Umdeckung um der Umdeckung willen", "Keine Vorabkosten für die Prüfung"],
    },
  ];

  const risiken = [
    "Das Ergebnis einer Prüfung hängt vom individuellen Vertrag ab – von Tarif, Restlaufzeit, Kosten und gewählter Lösung. Pauschale Erfolgsaussagen sind nicht möglich.",
    "Eine Rückabwicklung setzt rechtliche Voraussetzungen voraus (z. B. fehlerhafte Widerrufsbelehrung oder Beratungsmängel) und wird im Einzelfall geprüft – ein Anspruch besteht nicht automatisch.",
    "Bei einer Neuordnung der Vorsorge können je nach Lösung Kosten entstehen – diese werden vorab transparent offengelegt.",
    "Garantien, steuerliche Behandlung und Konditionen unterscheiden sich je nach Vertrag – verbindliche Aussagen erst nach Prüfung Ihrer Unterlagen.",
  ];

  const faq = [
    {
      q: "Was ist der Unterschied zwischen Brutto- und Nettopolice?",
      a: "In einer klassischen Bruttopolice sind Abschluss- und Vertriebsprovisionen in den Vertrag einkalkuliert – sie werden über Ihre Beiträge finanziert und belasten vor allem die ersten Jahre. Eine Nettopolice enthält diese einkalkulierten Provisionen nicht: Die Vergütung der Beratung erfolgt separat und transparent. Der Versicherungsschutz selbst ist identisch.",
    },
    {
      q: "Soll ich meinen alten Vertrag einfach kündigen?",
      a: "Nein – bitte nicht vorschnell. Eine Kündigung ist häufig der schlechteste Weg: Rückkaufswerte können niedrig ausfallen, Garantien und steuerliche Vorteile gehen unwiderruflich verloren. Lassen Sie den Vertrag erst prüfen – oft gibt es bessere Optionen wie Tarifwechsel oder Verwertung.",
    },
    {
      q: "Welche Unterlagen brauche ich für die Prüfung?",
      a: "Für den Einstieg genügen drei Angaben: Ihr Tarif bzw. Versicherer, der aktuelle Rückkaufswert (steht in Ihrer jährlichen Standmitteilung) und Ihre monatliche Sparrate. Alles Weitere klären wir gemeinsam.",
    },
    {
      q: "Was kostet die Prüfung?",
      a: "Die Erstprüfung ist kostenfrei und unverbindlich – es entstehen keine Vorabkosten. Sollten bei einer Umsetzung Kosten anfallen (z. B. Honorar bei einer Nettopolice), legen wir diese vorab schriftlich offen.",
    },
    {
      q: "Ist Eleven ein gebundener Vertreter?",
      a: "Nein. Der Eleven Maklerservice ist als Versicherungsmakler nach § 34d GewO tätig – ungebunden, mit Zugriff auf über 30 Anbieter von Nettotarifen. Wir arbeiten in Ihrem Auftrag, nicht im Auftrag eines Versicherers.",
    },
    {
      q: "Wie lange dauert die Verwertung eines Altvertrags?",
      a: "Beim Aufkauf über unseren Partner erfolgt die Abwicklung in der Regel innerhalb von 14 bis 21 Werktagen. Eine Rückabwicklungsprüfung kann länger dauern – sie wird häufig auf Erfolgsbasis durchgeführt.",
    },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="Eleven Maklerservice – Nettopolice & Prüfung bestehender Versicherungsverträge"
        description="Lebens- und Rentenversicherung prüfen lassen: Kosten transparent machen, Tarifwechsel und Verwertungsoptionen kennen – ohne vorschnelle Kündigung. Ungebundener Makler nach § 34d GewO."
      />

      {/* HERO */}
      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="ms-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Eleven Maklerservice</p>
              </motion.div>
              <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="ms-hero-headline">
                Mehr aus bestehenden Vorsorgeverträgen herausholen.
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[60ch]">
                Viele Lebens- und Rentenversicherungen enthalten einkalkulierte Abschluss- und Vertriebskosten, die den Sparanteil mindern. Wir prüfen Ihren Vertrag, machen Kosten transparent – und zeigen Ihnen alle Optionen. Ohne vorschnelle Kündigung.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="ms-hero-primary-cta">
                  <Link to="/anfrage/vertragspruefung">Vertrag prüfen lassen <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 border-white/25 text-white bg-transparent hover:bg-white/5 hover:text-white hover:border-white" data-testid="ms-hero-secondary-cta">
                  <Link to="/anfrage/nettopolice">Nettopolice anfragen</Link>
                </Button>
              </motion.div>
              <motion.div variants={fadeUp} className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-y-2.5 gap-x-6 max-w-2xl">
                <CheckItem dark>Keine Vorabkosten</CheckItem>
                <CheckItem dark>Konzept i. d. R. in 48 Stunden</CheckItem>
                <CheckItem dark>Makler nach § 34d GewO</CheckItem>
              </motion.div>
            </motion.div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-white/10 ">
                <img src={IMG_HERO} alt="Vertragsprüfung – Eleven Maklerservice" className="w-full h-[440px] object-cover" loading="eager" decoding="async" data-testid="ms-hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-white py-14 sm:py-20" data-testid="ms-problem">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <SectionHeading
              eyebrow="Das Problem"
              title="Was Ihr Vertrag wirklich kostet, steht selten auf Seite eins"
              subtitle="Abschluss- und Vertriebskosten werden in klassischen Verträgen über die Beiträge finanziert – vor allem in den ersten Jahren. Die Aufsichtsbehörde BaFin veröffentlicht regelmäßig Untersuchungen zu Effektivkosten von Lebensversicherungen. Was das für Ihren konkreten Vertrag bedeutet, zeigt nur eine individuelle Prüfung."
            />
            <div className="space-y-4">
              {[
                { icon: FileSearch, title: "Kosten sind schwer erkennbar", text: "Effektivkosten verteilen sich auf Abschluss-, Vertriebs- und Verwaltungskosten – verstreut über Vertragswerk und Standmitteilungen." },
                { icon: Scale, title: "Der Sparanteil leidet", text: "Jeder Euro, der in Kosten fließt, arbeitet nicht für Ihre Vorsorge. Gerade bei langen Laufzeiten summiert sich das." },
                { icon: XCircle, title: "Kündigung ist selten die Lösung", text: "Wer vorschnell kündigt, verliert häufig Garantien, steuerliche Vorteile – und erhält oft nur einen niedrigen Rückkaufswert." },
              ].map((p) => (
                <Reveal key={p.title} className="flex gap-4 rounded-[8px] border border-border bg-[#F6F7F8] p-6">
                  <div className="h-10 w-10 rounded-[6px] bg-[#111318] flex items-center justify-center shrink-0">
                    <p.icon className="h-4.5 w-4.5 h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111318]">{p.title}</p>
                    <p className="mt-1 text-sm text-[#3B4452] leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BRUTTO VS NETTO */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="ms-vergleich">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Die Alternative"
            title="Bruttopolice und Nettopolice im Vergleich"
            subtitle="Gleicher Versicherungsschutz – unterschiedliche Kostenlogik."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Reveal className="rounded-[8px] bg-white border border-border p-8" data-testid="ms-brutto-card">
              <Badge variant="outline" className="rounded-[6px] text-[#6B7280] border-[#D7DADF]">Klassische Bruttopolice</Badge>
              <ul className="mt-6 space-y-3.5 text-sm text-[#3B4452]">
                <li className="flex gap-2.5"><XCircle className="h-4 w-4 text-[#9AA1AB] shrink-0 mt-0.5" /> Abschluss- und Vertriebsprovisionen sind in den Vertrag einkalkuliert</li>
                <li className="flex gap-2.5"><XCircle className="h-4 w-4 text-[#9AA1AB] shrink-0 mt-0.5" /> Kosten belasten vor allem die ersten Vertragsjahre</li>
                <li className="flex gap-2.5"><XCircle className="h-4 w-4 text-[#9AA1AB] shrink-0 mt-0.5" /> Vergütung der Vermittlung ist im Beitrag „versteckt“</li>
                <li className="flex gap-2.5"><XCircle className="h-4 w-4 text-[#9AA1AB] shrink-0 mt-0.5" /> Rückkaufswert in frühen Jahren häufig niedrig</li>
              </ul>
            </Reveal>
            <Reveal className="rounded-[8px] bg-[#111318] noise-overlay p-8 ring-1 ring-[#FF3C20]/30" data-testid="ms-netto-card">
              <Badge variant="outline" className="rounded-[6px] bg-[#FF3C20]/10 text-[#FF3C20] border-[#FF3C20]/40">Nettopolice</Badge>
              <ul className="mt-6 space-y-3.5 text-sm text-white/70">
                <li className="flex gap-2.5"><BadgeCheck className="h-4 w-4 text-[#FF3C20] shrink-0 mt-0.5" /> Keine einkalkulierten Abschluss- und Vertriebsprovisionen im Vertrag</li>
                <li className="flex gap-2.5"><BadgeCheck className="h-4 w-4 text-[#FF3C20] shrink-0 mt-0.5" /> Mehr vom Beitrag fließt in den Sparanteil</li>
                <li className="flex gap-2.5"><BadgeCheck className="h-4 w-4 text-[#FF3C20] shrink-0 mt-0.5" /> Vergütung der Beratung separat und transparent vereinbart</li>
                <li className="flex gap-2.5"><BadgeCheck className="h-4 w-4 text-[#FF3C20] shrink-0 mt-0.5" /> Identischer Versicherungsschutz – Zugriff auf über 30 Nettotarif-Anbieter</li>
              </ul>
            </Reveal>
          </div>
          <div className="mt-8 max-w-4xl mx-auto">
            <ComplianceNote>
              Ob und in welchem Umfang sich eine Umstellung für Sie lohnt, hängt vom individuellen Vertrag ab – von Restlaufzeit, Kosten, Garantien und der gewählten Lösung. Konkrete Werte nennen wir erst nach Prüfung Ihrer Unterlagen; eine individuelle Hochrechnung erhalten Sie im Gespräch, klar als unverbindliche Modellrechnung gekennzeichnet.
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* DREI WEGE */}
      <section className="bg-white py-14 sm:py-20" data-testid="ms-wege">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Ihre Optionen"
            title="Drei Wege nach der Prüfung – Kündigung ist keiner davon"
            subtitle="Welcher Weg passt, zeigt die Analyse. Sie entscheiden – dokumentiert und ohne Druck."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {wege.map((w, i) => (
              <Reveal key={w.title} className="h-full">
                <div className="h-full rounded-[8px] border border-border bg-white p-8 card-hover flex flex-col" data-testid={`ms-weg-${i + 1}`}>
                  <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                    <w.icon className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-[#111318]">{w.title}</h3>
                  <p className="mt-3 text-sm text-[#3B4452] leading-relaxed flex-1">{w.text}</p>
                  <div className="mt-5 space-y-2.5">
                    {w.punkte.map((p) => (
                      <CheckItem key={p}>{p}</CheckItem>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABLAUF */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="ms-ablauf">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="So läuft die Prüfung"
            title="Von drei Angaben zum dokumentierten Ergebnis"
            subtitle="Kostenfrei, unverbindlich – und ohne dass Sie Ihren Vertrag anfassen müssen."
          />
          <div className="mt-12">
            <ProcessTimeline steps={ablauf} />
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {[
              { icon: ClipboardList, label: "3 Angaben genügen" },
              { icon: Clock, label: "Konzept i. d. R. in 48 h" },
              { icon: ShieldCheck, label: "Keine Vorabkosten" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 rounded-[6px] border border-border bg-white px-4 py-3">
                <b.icon className="h-4 w-4 text-[#E8241B]" />
                <span className="text-xs font-medium text-[#2A303A]">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RISIKEN & GRENZEN */}
      <section className="bg-white py-14 sm:py-20" data-testid="ms-risiken">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <SectionHeading
              eyebrow="Risiken & Grenzen"
              title="Was wir nicht versprechen können"
              subtitle="Eine seriöse Prüfung nennt auch ihre Grenzen – vor der Zusammenarbeit, nicht danach."
            />
            <div className="space-y-4">
              {risiken.map((r, i) => (
                <Reveal key={i} className="flex items-start gap-3.5 rounded-[6px] border border-border bg-[#F6F7F8] p-5" data-testid={`ms-risiko-${i + 1}`}>
                  <AlertTriangle className="h-4 w-4 text-[#E8241B] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#2A303A] leading-relaxed">{r}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="ms-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Häufige Fragen" title="Fragen zur Vertragsprüfung & Nettopolice" />
          <div className="mt-10">
            <FAQSection items={faq} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Lassen Sie Ihren Vertrag prüfen – bevor Sie ihn aufgeben."
        subtitle="Drei Angaben genügen: Tarif, Rückkaufswert, Sparrate. Sie erhalten in der Regel innerhalb von 48 Stunden ein dokumentiertes Ergebnis – kostenfrei und unverbindlich."
        primaryLabel="Vertragsprüfung anfragen"
        primaryTo="/anfrage/vertragspruefung"
        secondaryLabel="15 Minuten Erstgespräch buchen"
        secondaryTo="/termin"
      />
    </div>
  );
}
